/**
 * Facebook Pages Platform Media Restrictions
 * Based on Meta Graph API v25.0 specifications.
 *
 * @see https://developers.facebook.com/docs/graph-api/reference/page/feed/
 * @see https://developers.facebook.com/docs/graph-api/reference/page/photos/
 * @see https://developers.facebook.com/docs/graph-api/reference/page/videos/
 * @see https://developers.facebook.com/docs/video-api/guides/reels-publishing/
 */

export const FACEBOOK_RESTRICTIONS = {
  image: {
    // Hard upload cap; recommended ≤10 MB.
    maxFileSize: 30 * 1024 * 1024, // 30 MB
    allowedFormats: [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/bmp',
      'image/gif',
      'image/tiff',
    ],
    // Meta downsizes for delivery; no strict floor enforced by the API.
    minResolution: { width: 200, height: 200 },
    maxResolution: { width: 2048, height: 2048 },
    // 1.91:1 → 4:5 are shown without cropping in feed; outside still uploads.
    recommendedAspectRatio: {
      min: 0.8, // 4:5
      max: 1.91, // 1.91:1
    },
  },
  video: {
    // Resumable upload supports up to 10 GB.
    maxFileSize: 10 * 1024 * 1024 * 1024, // 10 GB
    allowedFormats: ['video/mp4', 'video/mov', 'video/quicktime'],
    minDuration: 1, // seconds
    maxDuration: 240 * 60, // 4 hours
    minResolution: { width: 600, height: 600 },
    maxResolution: { width: 4096, height: 2304 },
    frameRate: {
      min: 24,
      max: 60,
    },
  },
  reel: {
    // 30 reels published per Page per rolling 24h via the API.
    maxFileSize: 1 * 1024 * 1024 * 1024, // 1 GB (effective cap)
    allowedFormats: ['video/mp4', 'video/mov', 'video/quicktime'],
    minDuration: 3, // seconds
    maxDuration: 90, // seconds
    minResolution: { width: 540, height: 960 },
    recommendedResolution: { width: 1080, height: 1920 },
    aspectRatio: 9 / 16,
    frameRate: {
      min: 24,
      max: 60,
    },
    rateLimit: {
      // 30 published reels per Page per rolling 24h.
      perRollingDay: 30,
    },
  },
  multiPhoto: {
    maxItems: 10, // Practical product limit per feed post
    allowedTypes: ['image'],
    // Unpublished photos are auto-deleted after ~24h if not attached.
    unpublishedTtlMs: 24 * 60 * 60 * 1000,
  },
  story: {
    image: {
      maxFileSize: 4 * 1024 * 1024, // 4 MB; PNG ideally <1 MB
      allowedFormats: ['image/jpeg', 'image/jpg', 'image/png'],
      recommendedAspectRatio: 9 / 16,
    },
    video: {
      maxFileSize: 1 * 1024 * 1024 * 1024,
      allowedFormats: ['video/mp4', 'video/mov', 'video/quicktime'],
      minDuration: 3,
      maxDuration: 60,
      recommendedAspectRatio: 9 / 16,
    },
  },
  caption: {
    maxLength: 63206, // characters; same as the Facebook product cap
  },
  scheduling: {
    // /{page-id}/feed enforces 10 minutes ≤ scheduled_publish_time ≤ 75 days.
    minLeadTimeMs: 10 * 60 * 1000,
    maxLeadTimeMs: 75 * 24 * 60 * 60 * 1000,
  },
  general: {
    supportsDocuments: false,
    requiresMedia: false, // Text-only posts are allowed
  },
} as const

export type FacebookRestrictions = typeof FACEBOOK_RESTRICTIONS
